import React from 'react';
import Footer from '../components/Footer';
import Navbar from '../components/navbar/Navbar.js';
import Banner from '../components/Banner.js';
import FancyFeatureGrid from '../components/FancyFeatureGrid.js';
import TileGrid from '../components/TileGrid.js';
import ContentTile from '../components/ContentTile.js';
import gateway from '../images/products/gateway.webp';
import { useSEO } from '../components/CustomHook';

const Gateway = () => {
    useSEO(
        "Gateway - Intelligente Steuerung für Ihr Energiesystem | Schmid Energieberatung",
        "Entdecken Sie unsere Gateway-Systeme für die intelligente Steuerung und Überwachung Ihrer Energieanlage. Zentrale Kontrolle aller Komponenten mit modernster Technologie.",
        "Gateway, Energiesystem, Smart Home, Energieüberwachung, Solar Gateway, Energiemanagement",
        "/images/products/gateway.webp"
    );

    const modalTiles = [
        {
            title: "Zentrale Steuerung",
            content: "Das Herzstück Ihrer intelligenten Energieanlage",
            icon: "fas fa-cogs",
            modalContent: [
                "Unser Gateway-System bildet das Herzstück Ihrer intelligenten Energieanlage. Es ermöglicht Ihnen die zentrale Steuerung und Überwachung aller Komponenten Ihres Energiesystems - von Solarmodulen über Speichersysteme bis hin zur Wallbox.",
                "Durch die intelligente Vernetzung aller Systemkomponenten optimiert das Gateway automatisch Ihre Energieflüsse und sorgt für maximale Effizienz. So nutzen Sie Ihre selbst erzeugte Energie optimal und sparen bares Geld.",
                "Die moderne Benutzeroberfläche macht es einfach, den Überblick zu behalten und bei Bedarf manuelle Anpassungen vorzunehmen. Alle wichtigen Funktionen sind intuitiv bedienbar und bieten Ihnen maximale Kontrolle über Ihr Energiesystem."
            ]
        },
        {
            title: "Echtzeit-Monitoring",
            content: "Immer den Überblick über Ihre Energie",
            icon: "fas fa-chart-line",
            modalContent: [
                "Überwachen Sie Ihre Energieproduktion und -nutzung in Echtzeit. Das Gateway sammelt kontinuierlich Daten von allen Komponenten und stellt Ihnen detaillierte Analysen zur Verfügung.",
                "Durch die umfassende Datenerfassung können Sie genau sehen, wann und wie viel Energie produziert, gespeichert oder verbraucht wird. Diese Transparenz hilft Ihnen dabei, Ihre Energienutzung zu optimieren und Kosten zu sparen.",
                "Die übersichtlichen Dashboards und Grafiken machen komplexe Energiedaten leicht verständlich und handhabbar. So können Sie auf einen Blick erkennen, wo Optimierungspotential besteht und wie Sie Ihre Energieautarkie weiter steigern können."
            ]
        },
        {
            title: "Fernzugriff",
            content: "Kontrolle von überall auf der Welt",
            icon: "fas fa-mobile-alt",
            modalContent: [
                "Greifen Sie von überall auf Ihr Energiesystem zu. Über die App oder das Web-Interface haben Sie jederzeit den Überblick und die Kontrolle über Ihre Anlage.",
                "Ob Sie unterwegs sind oder im Urlaub - Sie können Ihre Energieanlage jederzeit überwachen und bei Bedarf Anpassungen vornehmen. So haben Sie immer die volle Kontrolle über Ihr Energiesystem.",
                "Die sichere Verbindung gewährleistet, dass nur Sie Zugriff auf Ihre Daten haben und Ihre Privatsphäre geschützt bleibt. Die verschlüsselte Datenübertragung sorgt für höchste Sicherheit."
            ]
        },
        {
            title: "Intelligente Alarmierung",
            content: "Sofortige Benachrichtigung bei Problemen",
            icon: "fas fa-bell",
            modalContent: [
                "Erhalten Sie sofortige Benachrichtigungen bei Störungen oder ungewöhnlichen Ereignissen. Das Gateway überwacht kontinuierlich alle Systemkomponenten und warnt Sie bei Problemen.",
                "Durch die frühzeitige Erkennung von Störungen können Sie schnell reagieren und größere Schäden vermeiden. Das schützt nicht nur Ihre Anlage, sondern auch Ihre Investition.",
                "Die Alarmierung erfolgt über verschiedene Kanäle - per App, E-Mail oder SMS - je nach Ihren Präferenzen. So verpassen Sie nie wichtige Informationen über den Zustand Ihrer Anlage."
            ]
        },
        {
            title: "Energieoptimierung",
            content: "Automatische Optimierung Ihrer Energieflüsse",
            icon: "fas fa-brain",
            modalContent: [
                "Das Gateway analysiert kontinuierlich Ihre Energieflüsse und optimiert diese automatisch für maximale Effizienz. So nutzen Sie Ihre selbst erzeugte Energie optimal.",
                "Durch intelligente Algorithmen lernt das System Ihre Verbrauchsgewohnheiten und passt die Energieverteilung entsprechend an. Das führt zu höheren Einsparungen und einer besseren Wirtschaftlichkeit.",
                "Die automatische Optimierung sorgt dafür, dass Sie Ihre Energieautarkie maximieren und unabhängiger von steigenden Strompreisen werden."
            ]
        },
        {
            title: "Datenanalyse",
            content: "Detaillierte Einblicke in Ihre Energienutzung",
            icon: "fas fa-chart-bar",
            modalContent: [
                "Erhalten Sie detaillierte Analysen Ihrer Energienutzung mit aussagekräftigen Grafiken und Berichten. So können Sie Ihre Energieeffizienz kontinuierlich verbessern.",
                "Die umfassende Datenanalyse zeigt Ihnen, wo Optimierungspotential besteht und wie Sie Ihre Energiekosten weiter reduzieren können. Nutzen Sie diese Erkenntnisse für eine noch effizientere Energienutzung.",
                "Exportieren Sie Ihre Daten für detaillierte Analysen oder für die Weitergabe an Ihren Energieberater. So optimieren Sie Ihre Anlage kontinuierlich."
            ]
        }
    ];

    return (
        <>
            <Navbar />
            <Banner
                imageSrc={gateway}
                title="Gateway"
                subtitle="Intelligente Steuerung für Ihr Energiesystem"
                description="Unser Gateway-System bildet das Herzstück Ihrer intelligenten Energieanlage. Es ermöglicht Ihnen die zentrale Steuerung und Überwachung aller Komponenten Ihres Energiesystems."
            />

            {/* Fancy Feature Grid */}
            <FancyFeatureGrid />

            {/* Textabschnitte */}
            <div className="w-full max-w-7xl mx-auto py-20 px-6">
                {/* Hauptüberschrift */}
                <div className="text-center mb-20">
                    <h2 className="text-lg sm:text-2xl lg:text-4xl xl:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                        Intelligente Steuerung – Maximale Kontrolle – Optimale Effizienz
                    </h2>
                    <h3 className="text-2xl md:text-3xl font-semibold text-primary mb-12">
                        Das Gehirn Ihrer Energieanlage
                    </h3>
                </div>

                {/* Intro-Text */}
                <div className="text-center mb-20">
                    <p className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
                        Ein modernes Energiesystem ohne intelligente Steuerung ist wie ein Auto ohne Lenkrad. Unser Gateway-System übernimmt die zentrale Koordination aller Komponenten und sorgt dafür, dass Ihre Energie optimal genutzt wird. Von der Produktion über die Speicherung bis hin zum Verbrauch - alles läuft harmonisch zusammen und maximiert Ihre Energieautarkie.
                    </p>
                </div>

                {/* 5-in-1 System */}
                <ContentTile
                    title="5-in-1 Systemintegration für maximale Effizienz"
                    content="Unser Gateway vereint alle wichtigen Funktionen in einem kompakten System: Wechselrichter-Steuerung, Batteriemanagement, Wallbox-Integration, Verbrauchsoptimierung und Fernüberwachung. So erhalten Sie eine vollständige Energielösung aus einer Hand, die nicht nur Platz spart, sondern auch die Kommunikation zwischen allen Komponenten optimiert."
                    gradient={true}
                    className="mb-20"
                />

                {/* Technische Spezifikationen */}
                <ContentTile
                    title="Hochmoderne Technologie mit bewährter Zuverlässigkeit"
                    content="Alle Stärken, die den Unterschied machen"
                    className="mb-20 text-center bg-secondary-light text-tertiary"
                />

                {/* Interaktive Kacheln */}
                <div className="mb-20">
                    <TileGrid tiles={modalTiles} />
                </div>

                {/* Systemintegration */}
                <ContentTile
                    title="Nahtlose Integration in Ihr Energiesystem"
                    content={
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
                            <div className="text-center bg-white p-6 rounded-lg shadow-md">
                                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <i className="fas fa-solar-panel text-white text-2xl"></i>
                                </div>
                                <h4 className="text-xl font-semibold mb-3">Solarmodule</h4>
                                <p className="text-gray-600">Überwachung der Solarproduktion und Optimierung der Erträge durch intelligente Steuerung der Wechselrichter.</p>
                            </div>
                            <div className="text-center bg-white p-6 rounded-lg shadow-md">
                                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <i className="fas fa-battery-full text-white text-2xl"></i>
                                </div>
                                <h4 className="text-xl font-semibold mb-3">Speichersysteme</h4>
                                <p className="text-gray-600">Intelligentes Batteriemanagement für optimale Ladung und Entladung entsprechend Ihren Verbrauchsgewohnheiten.</p>
                            </div>
                            <div className="text-center bg-white p-6 rounded-lg shadow-md">
                                <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <i className="fas fa-charging-station text-white text-2xl"></i>
                                </div>
                                <h4 className="text-xl font-semibold mb-3">Wallboxen</h4>
                                <p className="text-gray-600">Steuerung der E-Auto Ladung mit Priorisierung auf Solarstrom und optimale Nutzung günstiger Tarife.</p>
                            </div>
                            <div className="text-center bg-white p-6 rounded-lg shadow-md">
                                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <i className="fas fa-home text-white text-2xl"></i>
                                </div>
                                <h4 className="text-xl font-semibold mb-3">Haushaltsgeräte</h4>
                                <p className="text-gray-600">Intelligente Laststeuerung für optimale Nutzung der verfügbaren Energie und Minimierung der Netzbezugskosten.</p>
                            </div>
                        </div>
                    }
                    className="mb-20 bg-gradient-to-br from-blue-400 to-purple-500 text-white"
                />

                {/* Sicherheit und Datenschutz */}
                <ContentTile
                    title="Sicherheit und Datenschutz an erster Stelle"
                    content={
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
                            <div className="text-center">
                                <h4 className="text-2xl font-semibold mb-4">Verschlüsselte Datenübertragung nach höchsten Standards</h4>
                                <p className="text-lg">Ihre Energiedaten sind durch moderne Verschlüsselungstechnologien geschützt und bleiben privat. Wir verwenden die gleichen Sicherheitsstandards wie Banken und Finanzinstitute.</p>
                            </div>
                            <div className="text-center">
                                <h4 className="text-2xl font-semibold mb-4">DSGVO-konforme Datenspeicherung</h4>
                                <p className="text-lg">Alle Daten werden entsprechend der europäischen Datenschutzrichtlinien verarbeitet und gespeichert. Sie behalten die volle Kontrolle über Ihre Daten.</p>
                            </div>
                        </div>
                    }
                    className="mb-20 bg-gradient-to-br from-green-400 to-blue-500 text-white"
                />

                {/* Zukunftssicherheit */}
                <ContentTile
                    title="Zukunftssicher durch regelmäßige Updates und Erweiterbarkeit"
                    content="Unser Gateway-System wird kontinuierlich weiterentwickelt und mit neuen Funktionen ausgestattet. Durch regelmäßige Software-Updates bleiben Sie immer auf dem neuesten Stand der Technologie. Die modulare Bauweise ermöglicht es, neue Komponenten einfach zu integrieren und Ihr System bei Bedarf zu erweitern."
                    className="text-center bg-secondary text-tertiary"
                />
            </div>

            <Footer />
        </>
    );
};

export default Gateway; 